import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiCornerDownLeft, FiSearch } from "react-icons/fi";
import { flattenMenu, searchMenu } from "./menuModel";
import { iconFor } from "./icons";
import { OPEN_SEARCH_EVENT } from "./shellEvents";
import "./shell.css";

/**
 * Jump to any page the signed-in user can open. It searches the same filtered menu the sidebar
 * shows, so a role never finds a page it cannot reach.
 */
export default function MenuSearch({ items }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listId = useId();
  const returnFocus = useRef(null);

  const entries = useMemo(() => flattenMenu(items), [items]);
  const results = useMemo(() => searchMenu(entries, query), [entries, query]);

  useEffect(() => {
    const show = () => {
      returnFocus.current = document.activeElement;
      setQuery("");
      setActive(0);
      setOpen(true);
    };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        show();
      }
    };
    window.addEventListener(OPEN_SEARCH_EVENT, show);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener(OPEN_SEARCH_EVENT, show);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => setActive(0), [query]);

  const close = () => {
    setOpen(false);
    returnFocus.current?.focus?.();
  };

  const go = (entry) => {
    if (!entry) return;
    setOpen(false);
    navigate(entry.path);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    }
  };

  if (!open) return null;

  return (
    <div className="sx sx-pal-back" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="sx-pal" role="dialog" aria-modal="true" aria-label="Search the menu">
        <div className="sx-pal-in">
          <FiSearch size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={results[active] ? `${listId}-${active}` : undefined}
            aria-label="Search the menu"
            placeholder="Search pages"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        {results.length ? (
          <ul id={listId} role="listbox" aria-label="Pages">
            {results.map((entry, i) => {
              const Icon = iconFor({ path: entry.path });
              return (
                <li
                  key={`${entry.path}-${entry.label}`}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  onMouseMove={() => setActive(i)}
                  onClick={() => go(entry)}
                >
                  <Icon size={18} aria-hidden="true" />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="t">{entry.label}</span>
                    <span className="s">{entry.parent ? `${entry.parent}` : entry.group || ""}</span>
                  </span>
                  {i === active ? <FiCornerDownLeft size={14} aria-hidden="true" /> : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="none" role="status">No page matches "{query}".</div>
        )}
        <div className="sx-pal-foot" aria-hidden="true">
          <span><kbd>↑</kbd> <kbd>↓</kbd> to move</span>
          <span><kbd>Enter</kbd> to open</span>
          <span><kbd>Esc</kbd> to close</span>
        </div>
      </div>
    </div>
  );
}
